import React, { useEffect } from 'react';
import {
  Drawer,
  Box,
  Typography,
  List,
  ListItem,
  IconButton,
  Button,
  Badge
} from '@mui/material';
import {
  Close as CloseIcon,
  FiberManualRecord as UnreadIcon,
  Notifications as NotificationsIcon,
  CheckCircle as CheckIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useNotificationsStore } from '../../store/notificationsStore';

interface NotificationHeaderProps {
  open: boolean;
  onClose: () => void;
}

const NotificationHeader: React.FC<NotificationHeaderProps> = ({ open, onClose }) => {
  const navigate = useNavigate();
  const { unreadNotifications, markAsRead, getUnreadCount, fetchUnreadNotifications } = useNotificationsStore();
  const unreadCount = getUnreadCount();
  
  useEffect(() => {
    if (open) {
      fetchUnreadNotifications(50);
    }
  }, [open, fetchUnreadNotifications]);

  const handleViewAllNotifications = () => {
    navigate('/alerts');
    onClose();
  };

  const handleMarkAsRead = async (notificationId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    
    const notification = unreadNotifications.find(n => n.id === notificationId);
    if (notification && notification.isRead) {
      return;
    }
    await markAsRead(notificationId);
  };

  const handleNotificationClick = (notificationId: string) => {
    navigate(`/alerts?notification=${notificationId}`);
    onClose();
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: { xs: '100%', sm: 400 },
          maxWidth: 400,
        },
      }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ 
          p: 2, 
          borderBottom: '1px solid #e0e0e0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Badge badgeContent={unreadCount} color="primary">
              <NotificationsIcon color="primary" />
            </Badge>
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 700,
                fontSize: { xs: '14px', sm: '16px', md: '18px' },
                lineHeight: '100%',
                color: '#585858'
              }}
            >
              Notificaciones
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ flex: 1, overflow: 'auto' }}>
          {unreadNotifications.length > 0 ? (
            <List sx={{ p: 0 }}>
              {unreadNotifications.map((notification) => (
                <ListItem key={notification.id} disablePadding>
                  <Box
                    sx={{
                      width: '100%',
                      p: 2,
                      borderBottom: '1px solid #f5f5f5',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1,
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: '#f9f9f9',
                      },
                    }}
                    onClick={() => handleNotificationClick(notification.id)}
                  >
                    <UnreadIcon 
                      color="primary" 
                      sx={{ fontSize: 12, mt: 0.5, flexShrink: 0 }} 
                    />
                    
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontWeight: 700,
                          fontSize: { xs: '14px', sm: '16px', md: '18px' },
                          lineHeight: '100%',
                          color: '#585858',
                          mb: 0.5,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {notification.title}
                      </Typography>
                      
                      <Typography
                        sx={{
                          fontWeight: 400,
                          fontSize: { xs: '12px', sm: '14px', md: '16px' },
                          lineHeight: '100%',
                          color: 'text.secondary',
                          mb: 0.5,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                        }}
                      >
                        {notification.description}
                      </Typography>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
                        <Typography
                          sx={{
                            fontWeight: 400,
                            fontSize: { xs: '10px', sm: '12px', md: '14px' },
                            lineHeight: '100%',
                            color: 'text.secondary'
                          }}
                        >
                          {formatDate(notification.date)}
                        </Typography>
                        
                        <Button
                          size="small"
                          startIcon={<CheckIcon />}
                          onClick={(e) => handleMarkAsRead(notification.id, e)}
                          sx={{
                            textTransform: 'none',
                            fontSize: '12px',
                            minWidth: 'auto',
                            px: 1,
                            py: 0.5,
                            color: 'primary.main',
                            '&:hover': {
                              backgroundColor: 'primary.light',
                              color: 'white'
                            }
                          }}
                        >
                          Marcar como leída
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                </ListItem>
              ))}
            </List>
          ) : (
            <Box 
              sx={{ 
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                p: 3,
                textAlign: 'center'
              }}
            >
              <NotificationsIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2, opacity: 0.3 }} />
              <Typography 
                variant="h6" 
                sx={{ 
                  color: 'text.secondary',
                  mb: 1,
                  fontWeight: 600
                }}
              >
                No hay notificaciones nuevas
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ color: 'text.secondary' }}
              >
                Todas tus notificaciones están al día
              </Typography>
            </Box>
          )}
        </Box>

        <Box sx={{ p: 2, borderTop: '1px solid #e0e0e0' }}>
          <Button
            fullWidth
            variant="outlined"
            onClick={handleViewAllNotifications}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              py: 1.5
            }}
          >
            Ver todas las notificaciones
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

export default NotificationHeader;